// SPDX-License-Identifier: AGPL-3.0-or-later

import {Slate} from '@app/features/app/components/dialogs/components/Slate';
import {
	SettingsTabContainer,
	SettingsTabContent,
	SettingsTabHeader,
} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {DESKTOP_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import * as AuthSessionCommands from '@app/features/auth/commands/AuthSessionCommands';
import {DeviceRevokeModal} from '@app/features/auth/components/modals/DeviceRevokeModal';
import type {AuthSession} from '@app/features/auth/models/AuthSession';
import AuthSessionState from '@app/features/auth/state/AuthSession';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Spinner} from '@app/features/ui/components/Spinner';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import styles from '@app/features/user/components/modals/tabs/DevicesTab.module.css';
import {formatShortRelativeTime} from '@app/vendor/date_utils/DateDuration';
import {Trans, useLingui} from '@lingui/react/macro';
import {
	CheckIcon,
	CheckSquareIcon,
	DeviceMobileIcon,
	MonitorIcon,
	SquareIcon,
	WifiSlashIcon,
	XIcon,
} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useState} from 'react';

const MOBILE_DEVICE_REGEX = /iOS|Android|Windows Phone|BlackBerry|Mobile/i;
const StatusDot = observer(() => <div aria-hidden={true} className={styles.statusDot} />);
const CustomCheckbox = observer(({checked}: {checked: boolean}) => (
	<div className={clsx(styles.checkbox, checked ? styles.checkboxChecked : styles.checkboxUnchecked)}>
		{checked && <CheckIcon weight="regular" className={styles.checkIcon} />}
	</div>
));
interface AuthSessionProps {
	authSession: AuthSession;
	isCurrent?: boolean;
	isSelected?: boolean;
	onSelect?: (id: string, index: number, shiftKey: boolean) => void;
	index?: number;
	selectionMode?: boolean;
}
const AuthSessionItem: React.FC<AuthSessionProps> = observer(
	({authSession, isCurrent = false, isSelected, onSelect, index, selectionMode}) => {
		const {t} = useLingui();
		const isMobile = MOBILE_DEVICE_REGEX.test(authSession.clientOs);
		const isInteractive = selectionMode && !isCurrent && onSelect;
		const handleClick = isInteractive
			? (e: React.MouseEvent) => onSelect(authSession.id, index!, e.shiftKey)
			: undefined;
		const handleKeyDown = isInteractive
			? (e: React.KeyboardEvent) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						onSelect(authSession.id, index!, e.shiftKey);
					}
				}
			: undefined;
		const interactiveProps = isInteractive
			? {
					onClick: handleClick,
					onKeyDown: handleKeyDown,
					role: 'checkbox' as const,
					'aria-checked': Boolean(isSelected),
					tabIndex: 0,
				}
			: {};
		const platformLabel =
			authSession.clientPlatform === DESKTOP_PRODUCT_NAME ? DESKTOP_PRODUCT_NAME : authSession.clientPlatform;
		const hasLocation = Boolean(authSession.clientLocation);
		const locationRowVisible = hasLocation || !isCurrent;
		return (
			<div
				className={clsx(styles.authSession, selectionMode && !isCurrent && styles.authSessionSelectable)}
				{...interactiveProps}
			>
				<div className={styles.authSessionContent}>
					<div className={styles.iconContainer}>
						{isMobile ? <DeviceMobileIcon className={styles.icon} /> : <MonitorIcon className={styles.icon} />}
					</div>
					<div className={styles.authSessionInfo}>
						<span className={styles.authSessionTitle}>
							{authSession.clientOs}
							<StatusDot />
							{platformLabel}
						</span>
						{locationRowVisible && (
							<div className={styles.authSessionLocation}>
								{hasLocation && <span className={styles.locationText}>{authSession.clientLocation}</span>}
								{!isCurrent && hasLocation && <span aria-hidden className={styles.locationSeparator} />}
								{!isCurrent && (
									<span className={styles.lastUsed}>
										{formatShortRelativeTime(authSession.approxLastUsedAt ?? new Date(0))}
									</span>
								)}
							</div>
						)}
					</div>
				</div>
				<div className={styles.authSessionActions}>
					{!isCurrent && !selectionMode && (
						<Tooltip text={t`Revoke device`}>
							<button
								type="button"
								onClick={() =>
									ModalCommands.push(modal(() => <DeviceRevokeModal sessionIdHashes={[authSession.id]} />))
								}
								className={styles.revokeButton}
							>
								<XIcon className={styles.revokeIcon} weight="bold" />
							</button>
						</Tooltip>
					)}
					{selectionMode && !isCurrent && <CustomCheckbox checked={!!isSelected} />}
				</div>
			</div>
		);
	},
);
const DevicesTab: React.FC = observer(() => {
	const {t} = useLingui();
	const authSessionIdHash = AuthSessionState.authSessionIdHash;
	const authSessions = AuthSessionState.authSessions;
	const fetchStatus = AuthSessionState.fetchStatus;
	const otherDevices = authSessions.filter((authSession) => authSession.id !== authSessionIdHash);
	const [selectionMode, setSelectionMode] = useState(false);
	const [selectedDevices, setSelectedDevices] = useState(new Set<string>());
	const [lastToggledIndex, setLastToggledIndex] = useState(-1);
	useEffect(() => {
		AuthSessionCommands.fetch();
	}, []);
	const toggleSelectionMode = (value: boolean) => {
		setSelectionMode(value);
		if (!value) {
			setSelectedDevices(new Set());
			setLastToggledIndex(-1);
		}
	};
	const toggleDevice = (deviceId: string, index: number, isShiftSelect: boolean) => {
		if (!selectionMode) {
			toggleSelectionMode(true);
		}
		setSelectedDevices((prev) => {
			const newSelection = new Set(prev);
			if (isShiftSelect && lastToggledIndex !== -1) {
				const start = Math.min(lastToggledIndex, index);
				const end = Math.max(lastToggledIndex, index);
				const shouldAdd = !prev.has(deviceId);
				otherDevices.slice(start, end + 1).forEach((device) => {
					if (shouldAdd) {
						newSelection.add(device.id);
					} else {
						newSelection.delete(device.id);
					}
				});
			} else {
				if (prev.has(deviceId)) {
					newSelection.delete(deviceId);
				} else {
					newSelection.add(deviceId);
				}
			}
			return newSelection;
		});
		setLastToggledIndex(index);
	};
	const handleSelectAll = () => {
		if (selectedDevices.size === otherDevices.length) {
			setSelectedDevices(new Set());
		} else {
			setSelectedDevices(new Set(otherDevices.map((device) => device.id)));
		}
	};
	if (fetchStatus === 'idle' || fetchStatus === 'pending') {
		return (
			<div className={styles.loadingContainer}>
				<Spinner />
			</div>
		);
	}
	const currentSession = authSessions.find((authSession) => authSession.id === authSessionIdHash);
	if (fetchStatus === 'error' || !currentSession) {
		return (
			<Slate
				icon={WifiSlashIcon}
				title={t`Network Error`}
				description={t`We're having trouble connecting to the space-time continuum. Please check your connection and try again.`}
				buttonText={t`Retry`}
				onClick={() => AuthSessionCommands.fetch()}
			/>
		);
	}
	return (
		<SettingsTabContainer>
			<SettingsTabHeader
				title={<Trans>My Devices</Trans>}
				description={
					<Trans>
						See all devices that are currently logged into your account. Revoke any sessions that you don't recognize.
					</Trans>
				}
			/>
			<SettingsTabContent>
				<div className={styles.devicesSection}>
					<div className={styles.deviceGroup}>
						<h3 className={styles.deviceGroupTitle}>
							<Trans>Current Device</Trans>
						</h3>
						<AuthSessionItem authSession={currentSession} isCurrent={true} />
					</div>
					{otherDevices.length > 0 && (
						<div className={styles.deviceGroup}>
							<div className={styles.deviceGroupHeader}>
								<h3 className={styles.deviceGroupTitle}>
									<Trans>Other Devices</Trans>
								</h3>
								{otherDevices.length > 1 && (
									<div className={styles.actionsContainer}>
										<Tooltip text={selectionMode ? t`Exit Selection Mode` : t`Enter Selection Mode`}>
											<button
												type="button"
												onClick={() => toggleSelectionMode(!selectionMode)}
												className={styles.actionButton}
											>
												{selectionMode ? (
													<XIcon weight="bold" className={styles.actionIcon} />
												) : (
													<CheckSquareIcon className={styles.actionIcon} />
												)}
											</button>
										</Tooltip>
										{selectionMode && (
											<Tooltip text={selectedDevices.size === otherDevices.length ? t`Clear Selection` : t`Select All`}>
												<button type="button" onClick={handleSelectAll} className={styles.actionButton}>
													{selectedDevices.size === otherDevices.length ? (
														<SquareIcon className={styles.actionIcon} />
													) : (
														<CheckSquareIcon className={styles.actionIcon} />
													)}
												</button>
											</Tooltip>
										)}
									</div>
								)}
							</div>
							<div className={styles.devicesGrid}>
								{otherDevices.map((authSession, index) => (
									<AuthSessionItem
										key={authSession.id}
										authSession={authSession}
										isSelected={selectedDevices.has(authSession.id)}
										onSelect={toggleDevice}
										index={index}
										selectionMode={selectionMode}
									/>
								))}
							</div>
							{(otherDevices.length > 1 || (selectionMode && selectedDevices.size > 0)) && (
								<div className={styles.logoutSection}>
									<Button
										variant="danger-secondary"
										onClick={() => {
											if (selectionMode && selectedDevices.size > 0) {
												ModalCommands.push(
													modal(() => <DeviceRevokeModal sessionIdHashes={Array.from(selectedDevices)} />),
												);
											} else {
												ModalCommands.push(
													modal(() => (
														<DeviceRevokeModal sessionIdHashes={otherDevices.map((authSession) => authSession.id)} />
													)),
												);
											}
										}}
									>
										{selectionMode && selectedDevices.size > 0 ? (
											selectedDevices.size === 1 ? (
												<Trans>Log out 1 device</Trans>
											) : (
												<Trans>Log out {selectedDevices.size} devices</Trans>
											)
										) : (
											<Trans>Log Out All Other Devices</Trans>
										)}
									</Button>
									<p className={styles.logoutDescription}>
										<Trans>You'll have to log back in on all logged out devices</Trans>
									</p>
								</div>
							)}
						</div>
					)}
				</div>
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});

export default DevicesTab;
